/**
 * Unified Marketplace Dashboard
 * Combines Finance and Insurance stats in one view
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Progress } from '../../../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { 
  TrendingUp, TrendingDown, DollarSign, Users, FileText, Shield, 
  Banknote, ArrowUpRight, ArrowDownRight, RefreshCw, ChevronRight,
  PieChart, BarChart3, Activity, Target, Wallet, Clock, CheckCircle2,
  AlertCircle, Building2, Loader2, Calendar, Bell
} from 'lucide-react';
import { LeadTrendsChart, RevenueTrendsChart, CombinedBarChart, TotalRevenueChart } from '../../../components/marketplace/MarketplaceCharts';
import LeadNotificationSettings from '../../../components/marketplace/LeadNotificationSettings';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MarketplaceDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [financeData, setFinanceData] = useState(null);
  const [insuranceData, setInsuranceData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [trendsPeriod, setTrendsPeriod] = useState('7d');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    fetchTrends();
  }, [trendsPeriod]);

  const fetchAllData = async () => {
    try {
      setRefreshing(true);
      
      // Fetch both finance and insurance data in parallel
      const [financeRes, insuranceRes, trendsRes] = await Promise.all([
        axios.get(`${API_URL}/api/finance/admin/analytics`).catch(() => ({ data: { success: false } })),
        axios.get(`${API_URL}/api/insurance/admin/analytics`).catch(() => ({ data: { success: false } })),
        axios.get(`${API_URL}/api/insurance/marketplace/analytics/trends?period=${trendsPeriod}`).catch(() => ({ data: { success: false } }))
      ]);

      if (financeRes.data.success) {
        setFinanceData(financeRes.data.analytics);
      }
      if (insuranceRes.data.success) {
        setInsuranceData(insuranceRes.data.analytics);
      }
      if (trendsRes.data.success) {
        setTrendsData(trendsRes.data);
      }
      
    } catch (err) {
      console.error('Error fetching marketplace data:', err);
      setError('Failed to load marketplace data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchTrends = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/insurance/marketplace/analytics/trends?period=${trendsPeriod}`);
      if (response.data.success) {
        setTrendsData(response.data);
      }
    } catch (err) {
      console.error('Error fetching trends:', err);
    }
  };

  // Calculate unified metrics
  const getUnifiedMetrics = () => {
    const finance = financeData || {};
    const insurance = insuranceData || {};

    // Parse revenue values (remove $ and commas)
    const parseRevenue = (val) => {
      if (typeof val === 'number') return val;
      if (typeof val === 'string') {
        return parseFloat(val.replace(/[$,]/g, '')) || 0;
      }
      return 0;
    };

    const financeRevenue = parseRevenue(finance.total_revenue) || parseRevenue(finance.lead_revenue) || 0;
    const insuranceRevenue = parseRevenue(insurance.total_revenue) || parseRevenue(insurance.lead_revenue) || 0;
    const totalRevenue = financeRevenue + insuranceRevenue;

    const financeLeads = finance.total_applications || finance.total_leads || 0;
    const insuranceLeads = insurance.total_leads || 0;
    const totalLeads = financeLeads + insuranceLeads;

    const financeProviders = finance.active_lenders || finance.active_providers || 0;
    const insuranceProviders = insurance.active_providers || 0;
    const totalProviders = financeProviders + insuranceProviders;

    const financePending = finance.pending_verification || 0;
    const insurancePending = insurance.pending_verification || 0;
    const totalPending = financePending + insurancePending;

    const financeApproved = finance.approved || 0;
    const insurancePolicies = insurance.policies_issued || 0;
    const totalConversions = financeApproved + insurancePolicies;

    const financeConversion = finance.conversion_rate || (financeLeads > 0 ? ((financeApproved / financeLeads) * 100).toFixed(1) : 0);
    const insuranceConversion = insurance.conversion_rate || (insuranceLeads > 0 ? ((insurancePolicies / insuranceLeads) * 100).toFixed(1) : 0);
    const avgConversion = totalLeads > 0 ? ((totalConversions / totalLeads) * 100).toFixed(1) : 0;

    return {
      totalRevenue,
      financeRevenue,
      insuranceRevenue,
      totalLeads,
      financeLeads,
      insuranceLeads,
      totalProviders,
      financeProviders,
      insuranceProviders,
      totalPending,
      financePending,
      insurancePending,
      totalConversions,
      financeApproved,
      insurancePolicies,
      avgConversion,
      financeConversion,
      insuranceConversion
    };
  };

  const metrics = getUnifiedMetrics();

  // Revenue breakdown for pie chart visualization
  const revenueBreakdown = [
    { name: 'Finance', value: metrics.financeRevenue, color: 'bg-emerald-500' },
    { name: 'Insurance', value: metrics.insuranceRevenue, color: 'bg-indigo-500' }
  ];

  const totalRevenueForPercent = metrics.totalRevenue || 1;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6" data-testid="marketplace-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <PieChart className="h-8 w-8 text-purple-400" />
            Marketplace Dashboard
          </h1>
          <p className="text-gray-400 mt-1">Unified view of Finance & Insurance performance</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAllData}
            disabled={refreshing}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/dashboard')}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            Back to Admin
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white/10 mb-6">
          <TabsTrigger value="overview" className="data-[state=active]:bg-purple-600">
            <PieChart className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-purple-600">
            <Bell className="h-4 w-4 mr-2" />
            Notifications
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {/* Top Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* Total Revenue */}
        <Card className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-emerald-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-200 text-sm font-medium">Total Revenue</p>
                <p className="text-3xl font-bold text-white mt-1">
                  ${metrics.totalRevenue.toLocaleString()}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-emerald-600/50 text-emerald-100 text-xs">
                    <Banknote className="h-3 w-3 mr-1" />
                    ${metrics.financeRevenue.toLocaleString()}
                  </Badge>
                  <Badge className="bg-indigo-600/50 text-indigo-100 text-xs">
                    <Shield className="h-3 w-3 mr-1" />
                    ${metrics.insuranceRevenue.toLocaleString()}
                  </Badge>
                </div>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-500/30 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-emerald-300" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Leads */}
        <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-200 text-sm font-medium">Total Leads</p>
                <p className="text-3xl font-bold text-white mt-1">{metrics.totalLeads}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-emerald-600/50 text-emerald-100 text-xs">
                    Finance: {metrics.financeLeads}
                  </Badge>
                  <Badge className="bg-indigo-600/50 text-indigo-100 text-xs">
                    Insurance: {metrics.insuranceLeads}
                  </Badge>
                </div>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-500/30 flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-300" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Conversion Rate */}
        <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-200 text-sm font-medium">Avg Conversion</p>
                <p className="text-3xl font-bold text-white mt-1">{metrics.avgConversion}%</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-emerald-600/50 text-emerald-100 text-xs">
                    F: {metrics.financeConversion}%
                  </Badge>
                  <Badge className="bg-indigo-600/50 text-indigo-100 text-xs">
                    I: {metrics.insuranceConversion}%
                  </Badge>
                </div>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-500/30 flex items-center justify-center">
                <Target className="h-6 w-6 text-purple-300" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Providers */}
        <Card className="bg-gradient-to-br from-orange-500/20 to-amber-500/20 border-orange-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-200 text-sm font-medium">Active Providers</p>
                <p className="text-3xl font-bold text-white mt-1">{metrics.totalProviders}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-emerald-600/50 text-emerald-100 text-xs">
                    Lenders: {metrics.financeProviders}
                  </Badge>
                  <Badge className="bg-indigo-600/50 text-indigo-100 text-xs">
                    Insurers: {metrics.insuranceProviders}
                  </Badge>
                </div>
              </div>
              <div className="w-12 h-12 rounded-full bg-orange-500/30 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-orange-300" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Breakdown */}
        <Card className="bg-white/5 border-white/10 lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <PieChart className="h-5 w-5 text-purple-400" />
              Revenue Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {revenueBreakdown.map((item, idx) => {
                const percent = ((item.value / totalRevenueForPercent) * 100).toFixed(1);
                return (
                  <div key={idx}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-300 text-sm flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${item.color}`} />
                        {item.name}
                      </span>
                      <span className="text-white font-medium">${item.value.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Progress value={parseFloat(percent)} className="h-2 flex-1" />
                      <span className="text-gray-400 text-xs w-12">{percent}%</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quick Stats */}
            <div className="mt-6 pt-4 border-t border-white/10">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-white/5 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-400">{metrics.totalConversions}</p>
                  <p className="text-xs text-gray-400">Total Conversions</p>
                </div>
                <div className="text-center p-3 bg-white/5 rounded-lg">
                  <p className="text-2xl font-bold text-amber-400">{metrics.totalPending}</p>
                  <p className="text-xs text-gray-400">Pending Verification</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Module Performance */}
        <Card className="bg-white/5 border-white/10 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-400" />
              Module Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="bg-white/10 mb-4">
                <TabsTrigger value="overview" className="data-[state=active]:bg-purple-600">Overview</TabsTrigger>
                <TabsTrigger value="finance" className="data-[state=active]:bg-emerald-600">Finance</TabsTrigger>
                <TabsTrigger value="insurance" className="data-[state=active]:bg-indigo-600">Insurance</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="grid grid-cols-2 gap-4">
                  {/* Finance Summary */}
                  <div 
                    className="p-4 bg-gradient-to-br from-emerald-500/10 to-green-500/10 rounded-lg border border-emerald-500/20 cursor-pointer hover:border-emerald-400/40 transition-all"
                    onClick={() => navigate('/admin/finance')}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Banknote className="h-5 w-5 text-emerald-400" />
                        <span className="text-white font-medium">Finance Module</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-400">Revenue</p>
                        <p className="text-lg font-semibold text-emerald-400">${metrics.financeRevenue.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Leads</p>
                        <p className="text-lg font-semibold text-white">{metrics.financeLeads}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Approved</p>
                        <p className="text-lg font-semibold text-green-400">{metrics.financeApproved}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Lenders</p>
                        <p className="text-lg font-semibold text-white">{metrics.financeProviders}</p>
                      </div>
                    </div>
                  </div>

                  {/* Insurance Summary */}
                  <div 
                    className="p-4 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-lg border border-indigo-500/20 cursor-pointer hover:border-indigo-400/40 transition-all"
                    onClick={() => navigate('/admin/insurance')}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-indigo-400" />
                        <span className="text-white font-medium">Insurance Module</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-400">Revenue</p>
                        <p className="text-lg font-semibold text-indigo-400">${metrics.insuranceRevenue.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Leads</p>
                        <p className="text-lg font-semibold text-white">{metrics.insuranceLeads}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Policies</p>
                        <p className="text-lg font-semibold text-green-400">{metrics.insurancePolicies}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Insurers</p>
                        <p className="text-lg font-semibold text-white">{metrics.insuranceProviders}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pending Actions */}
                {metrics.totalPending > 0 && (
                  <div className="mt-4 p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                    <div className="flex items-center gap-2 text-amber-400">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">Pending Actions</span>
                    </div>
                    <div className="flex items-center gap-4 mt-2">
                      {metrics.financePending > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-amber-300 hover:text-amber-200"
                          onClick={() => navigate('/admin/finance')}
                        >
                          {metrics.financePending} lender verifications
                        </Button>
                      )}
                      {metrics.insurancePending > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-amber-300 hover:text-amber-200"
                          onClick={() => navigate('/admin/insurance')}
                        >
                          {metrics.insurancePending} insurer verifications
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="finance">
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-white/5 rounded-lg">
                      <p className="text-gray-400 text-sm">Applications</p>
                      <p className="text-2xl font-bold text-white">{financeData?.total_applications || 0}</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg">
                      <p className="text-gray-400 text-sm">Approved</p>
                      <p className="text-2xl font-bold text-green-400">{financeData?.approved || 0}</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg">
                      <p className="text-gray-400 text-sm">Disbursed</p>
                      <p className="text-2xl font-bold text-emerald-400">{financeData?.disbursed || 0}</p>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => navigate('/admin/finance')}
                  >
                    <Banknote className="h-4 w-4 mr-2" />
                    Open Finance Dashboard
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="insurance">
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-white/5 rounded-lg">
                      <p className="text-gray-400 text-sm">Leads</p>
                      <p className="text-2xl font-bold text-white">{insuranceData?.total_leads || 0}</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg">
                      <p className="text-gray-400 text-sm">Quoted</p>
                      <p className="text-2xl font-bold text-blue-400">{insuranceData?.quoted || 0}</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg">
                      <p className="text-gray-400 text-sm">Policies Issued</p>
                      <p className="text-2xl font-bold text-indigo-400">{insuranceData?.policies_issued || 0}</p>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => navigate('/admin/insurance')}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Open Insurance Dashboard
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Charts Section */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Activity className="h-5 w-5 text-purple-400" />
            Growth Analytics
          </h2>
          <div className="flex items-center gap-3">
            <Select value={trendsPeriod} onValueChange={setTrendsPeriod}>
              <SelectTrigger className="w-32 bg-white/10 border-white/20 text-white">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Growth Summary Cards */}
        {trendsData?.summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <p className="text-gray-400 text-xs mb-1">Period Leads</p>
                <p className="text-2xl font-bold text-white">{trendsData.summary.total_leads}</p>
                <div className="flex items-center mt-1">
                  {trendsData.summary.lead_growth_percent >= 0 ? (
                    <TrendingUp className="h-3 w-3 text-green-400 mr-1" />
                  ) : (
                    <TrendingDown className="h-3 w-3 text-red-400 mr-1" />
                  )}
                  <span className={`text-xs ${trendsData.summary.lead_growth_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {trendsData.summary.lead_growth_percent}%
                  </span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <p className="text-gray-400 text-xs mb-1">Period Revenue</p>
                <p className="text-2xl font-bold text-emerald-400">${trendsData.summary.total_revenue}</p>
                <div className="flex items-center mt-1">
                  {trendsData.summary.revenue_growth_percent >= 0 ? (
                    <TrendingUp className="h-3 w-3 text-green-400 mr-1" />
                  ) : (
                    <TrendingDown className="h-3 w-3 text-red-400 mr-1" />
                  )}
                  <span className={`text-xs ${trendsData.summary.revenue_growth_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {trendsData.summary.revenue_growth_percent}%
                  </span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <p className="text-gray-400 text-xs mb-1">Avg Daily Leads</p>
                <p className="text-2xl font-bold text-blue-400">{trendsData.summary.avg_daily_leads}</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <p className="text-gray-400 text-xs mb-1">Avg Daily Revenue</p>
                <p className="text-2xl font-bold text-purple-400">${trendsData.summary.avg_daily_revenue}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Lead Trends Chart */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-400" />
                Lead Trends
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trendsData?.trends ? (
                <LeadTrendsChart data={trendsData.trends} height={250} />
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Revenue Trends Chart */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-emerald-400" />
                Revenue Trends
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trendsData?.trends ? (
                <RevenueTrendsChart data={trendsData.trends} height={250} />
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Combined Module Comparison */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-purple-400" />
                Module Comparison
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trendsData?.trends ? (
                <CombinedBarChart data={trendsData.trends} height={250} />
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Total Revenue Area Chart */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Wallet className="h-4 w-4 text-amber-400" />
                Total Revenue Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trendsData?.trends ? (
                <TotalRevenueChart data={trendsData.trends} height={250} />
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button
            variant="outline"
            className="h-auto py-4 flex-col bg-white/5 border-white/10 text-white hover:bg-emerald-500/20 hover:border-emerald-500/40"
            onClick={() => navigate('/admin/finance')}
          >
            <Banknote className="h-6 w-6 mb-2 text-emerald-400" />
            <span>Finance Admin</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-4 flex-col bg-white/5 border-white/10 text-white hover:bg-indigo-500/20 hover:border-indigo-500/40"
            onClick={() => navigate('/admin/insurance')}
          >
            <Shield className="h-6 w-6 mb-2 text-indigo-400" />
            <span>Insurance Admin</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-4 flex-col bg-white/5 border-white/10 text-white hover:bg-blue-500/20 hover:border-blue-500/40"
            onClick={() => navigate('/lender/register')}
          >
            <Users className="h-6 w-6 mb-2 text-blue-400" />
            <span>Add Lender</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-4 flex-col bg-white/5 border-white/10 text-white hover:bg-purple-500/20 hover:border-purple-500/40"
            onClick={() => navigate('/insurer/register')}
          >
            <Building2 className="h-6 w-6 mb-2 text-purple-400" />
            <span>Add Insurer</span>
          </Button>
        </div>
      </div>
        </TabsContent>

        <TabsContent value="notifications">
          <LeadNotificationSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MarketplaceDashboard;
